import {
  useId, useState, type ReactNode, type RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  readStudioTimelineOpen,
  writeStudioTimelineOpen,
} from '@/lib/ai-assistant/studioTimelinePreference';
import { AssistantChat } from './AssistantPanel';

interface Props {
  /** Top bar (export) and the studio bar (clip, undo, full editor). */
  header: ReactNode;
  player: ReactNode;
  transport: ReactNode;
  aspect: 'portrait' | 'landscape';
  /** Player plus transport: fullscreen keeps the controls. */
  fullscreenRef: RefObject<HTMLDivElement>;
  /** The editor's timeline (scenes, layers, effects), collapsible. */
  timeline?: ReactNode;
  /** Controls for the layer or effect selected in the timeline. */
  selection?: ReactNode;
}

// Accessing localStorage itself can throw (blocked site data).
const browserStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

// AI Studio: the clip's preview, the assistant chat and the editor's timeline.
// The page fills the viewport under the app header (h-16), and the player is
// sized from its frame (container units), so it never overflows.
// - From `lg` the player takes the room left of a fixed chat column; the
//   timeline sits under it and the column scrolls once the player reaches its
//   minimum height.
// - Below `lg` (phones, and the mobile app's IA view, which is this page in a
//   WebView) the video and the chat share the screen: the editor column takes
//   46% of it and the player fills that column, so it fits any phone; the
//   timeline is under the player and the column scrolls to reach it. The chat
//   takes the rest.
export function AiStudioLayout({
  header, player, transport, aspect, fullscreenRef, timeline, selection,
}: Props) {
  const { t } = useTranslation();
  const timelineId = useId();
  const [timelineOpen, setTimelineOpen] = useState(() => readStudioTimelineOpen(browserStorage()));
  const toggleTimeline = () => {
    const next = !timelineOpen;
    setTimelineOpen(next);
    writeStudioTimelineOpen(browserStorage(), next);
  };

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col bg-background" data-testid="ai-studio">
      {header}
      <div className="flex min-h-0 flex-1 flex-col lg:grid lg:grid-cols-[minmax(0,1fr)_440px]">
        <div
          className="flex h-[46%] min-h-0 min-w-0 shrink-0 flex-col overflow-y-auto lg:h-auto lg:shrink"
          data-testid="ai-studio-editor"
        >
          <section
            ref={fullscreenRef}
            className="flex h-full min-h-0 min-w-0 shrink-0 flex-col gap-2 bg-background p-2 sm:p-4 lg:h-auto lg:min-h-[320px] lg:flex-1 lg:shrink lg:gap-3"
          >
            <div
              className="flex min-h-0 flex-1 items-center justify-center rounded-xl bg-black/40 p-1 [container-type:size] sm:p-2"
              data-testid="ai-studio-player"
            >
              <div
                className={cn(
                  'relative overflow-hidden rounded-lg',
                  aspect === 'portrait'
                    ? 'aspect-[9/16] h-[min(100cqh,177.78cqw)]'
                    : 'aspect-video w-[min(100cqw,177.78cqh)]',
                )}
              >
                {player}
              </div>
            </div>
            <div className="mx-auto w-full max-w-3xl">{transport}</div>
          </section>
          {selection && (
            // No overflow-* here: the layer Style popover needs the room to
            // place itself (same rule as the editor's selection row).
            <div className="flex shrink-0 justify-center px-3 pb-3 sm:px-4">
              <div className="max-w-full">{selection}</div>
            </div>
          )}
          {timeline && (
            <div className="shrink-0 border-t border-border px-3 pb-3 sm:px-4">
              <button
                type="button"
                className="flex w-full items-center gap-2 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                aria-expanded={timelineOpen}
                aria-controls={timelineId}
                onClick={toggleTimeline}
                data-testid="ai-studio-timeline-toggle"
              >
                <ChevronDown
                  className={cn('h-4 w-4 transition-transform', !timelineOpen && '-rotate-90')}
                  aria-hidden
                />
                {t('aiAssistant.studio.timeline')}
              </button>
              {timelineOpen && <div id={timelineId}>{timeline}</div>}
            </div>
          )}
        </div>
        <aside
          className="min-h-0 flex-1 border-t border-border bg-card lg:border-l lg:border-t-0"
          aria-label="AI Studio"
          data-testid="ai-studio-chat"
        >
          <AssistantChat variant="studio" />
        </aside>
      </div>
    </div>
  );
}
